import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppHeader from "../components/AppHeader";
import SiteFooter from "../components/SiteFooter";
import DisclaimerBanner from "../components/DisclaimerBanner";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { Scales, Suitcase, Gavel, EnvelopeOpen, CheckCircle, XCircle, Clock, Warning, Lock, ArrowRight, ChartLineUp, Gift } from "@phosphor-icons/react";
import { track } from "../lib/analytics";
import { MarketplaceNotice } from "../components/RequiredNotices";

const inputCls = "rounded-none border-slate-300 focus:border-slate-900 focus:ring-1 focus:ring-slate-900";

function OnboardForm({ onDone }) {
  const [form, setForm] = useState({ firm_name: "", bar_state: "", bar_number: "", practice_areas: "", bio: "" });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.firm_name || !form.bar_state || !form.bar_number) {
      toast.error("Firm name, bar state and bar number are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        firm_name: form.firm_name,
        bar_state: form.bar_state,
        bar_number: form.bar_number,
        practice_areas: form.practice_areas.split(",").map((s) => s.trim()).filter(Boolean),
        bio: form.bio,
      };
      await api.post("/lawyer/profile", payload);
      track("lawyer_signup", { bar_state: form.bar_state });
      toast.success("Lawyer profile saved");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-slate-200 p-8 max-w-3xl" data-testid="lawyer-onboard-form">
      <div className="label-tiny mb-2">Attorney onboarding</div>
      <h2 className="font-serif-display text-3xl text-slate-900 mb-4">Attorney Access Portal</h2>
      <p className="text-slate-600 mb-6">Browse anonymized civil cases filed on Prose.claims. Subscribe ($99/mo via Stripe) to unlock requests at $50 per case.</p>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-2"><Label className="label-tiny">Firm Name</Label><Input className={inputCls} value={form.firm_name} onChange={(e) => setForm({ ...form, firm_name: e.target.value })} data-testid="lawyer-firm-name" /></div>
        <div className="space-y-2"><Label className="label-tiny">Bar State</Label><Input className={inputCls} value={form.bar_state} onChange={(e) => setForm({ ...form, bar_state: e.target.value })} placeholder="e.g., CA" data-testid="lawyer-bar-state" /></div>
        <div className="space-y-2"><Label className="label-tiny">Bar Number</Label><Input className={inputCls} value={form.bar_number} onChange={(e) => setForm({ ...form, bar_number: e.target.value })} data-testid="lawyer-bar-number" /></div>
        <div className="space-y-2"><Label className="label-tiny">Practice Areas (comma-separated)</Label><Input className={inputCls} value={form.practice_areas} onChange={(e) => setForm({ ...form, practice_areas: e.target.value })} placeholder="Housing, Contract, Employment" data-testid="lawyer-practice-areas" /></div>
        <div className="md:col-span-2 space-y-2"><Label className="label-tiny">Bio</Label><Textarea className={`${inputCls} min-h-[100px]`} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} data-testid="lawyer-bio" /></div>
      </div>
      <div className="mt-6 flex justify-end">
        <Button onClick={submit} disabled={saving} className="rounded-none bg-slate-900 hover:bg-slate-800 text-white" data-testid="lawyer-save-profile">
          {saving ? "Saving…" : "Continue"}
        </Button>
      </div>
      <p className="text-xs text-slate-500 mt-4">By joining, you acknowledge this platform does not create an attorney–client relationship. Applications require admin approval before you can subscribe and browse cases.</p>
    </div>
  );
}

function SubscribeGate({ onSubscribe, lawyerStatus }) {
  const [busy, setBusy] = useState(false);

  if (lawyerStatus === "pending") {
    return (
      <div className="border border-amber-200 bg-amber-50 p-8 max-w-2xl" data-testid="lawyer-pending-approval">
        <Clock weight="duotone" className="w-10 h-10 text-amber-700" />
        <h3 className="font-serif-display text-3xl mt-3 text-amber-900">Application under review</h3>
        <p className="text-amber-900 mt-2">Your attorney profile has been submitted and is awaiting admin approval. We'll notify you by email once reviewed. Admins can approve at any time via the admin console.</p>
      </div>
    );
  }
  if (lawyerStatus === "rejected") {
    return (
      <div className="border border-red-200 bg-red-50 p-8 max-w-2xl" data-testid="lawyer-rejected">
        <XCircle weight="duotone" className="w-10 h-10 text-red-700" />
        <h3 className="font-serif-display text-3xl mt-3 text-red-900">Application not approved</h3>
        <p className="text-red-900 mt-2">Your application was not approved. Contact corporate@mishertech.com if you believe this is in error.</p>
      </div>
    );
  }

  const subscribe = async () => {
    setBusy(true);
    try {
      const r = await api.post("/payments/subscription/checkout", { origin_url: window.location.origin });
      if (r.data?.url) {
        track("payment_started_sub", { session_id: r.data.session_id });
        window.location.href = r.data.url;
        return;
      }
      toast.error("Could not start checkout");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not subscribe");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="border border-slate-200 p-8 max-w-2xl" data-testid="lawyer-subscribe-gate">
      <Lock weight="duotone" className="w-10 h-10 text-slate-700" />
      <h3 className="font-serif-display text-3xl mt-3">Activate subscription</h3>
      <p className="text-slate-600 mt-2">Subscribe to browse anonymized case listings. Per-unlock fees ($50) apply when you request an intro.</p>
      <div className="mt-4">
        <Button onClick={subscribe} disabled={busy} className="rounded-none bg-slate-900 hover:bg-slate-800 text-white" data-testid="lawyer-subscribe-btn">
          {busy ? "Starting checkout…" : "Subscribe · $99/mo"}
        </Button>
      </div>
      <p className="text-xs text-slate-500 mt-3">Secured by Stripe. You'll be redirected to complete payment and returned to your dashboard.</p>
    </div>
  );
}

function CaseBrowser({ onRefresh, freeAvailable }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(null);
  const [formationFilter, setFormationFilter] = useState("");
  const [appliedFilter, setAppliedFilter] = useState("");

  const load = async (filter = "") => {
    setLoading(true);
    try {
      const params = filter ? { state_of_formation: filter } : {};
      const r = await api.get("/lawyer/cases", { params });
      setItems(r.data);
    } catch (e) {
      toast.error("Could not load cases");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const applyFilter = () => {
    const f = formationFilter.trim();
    setAppliedFilter(f);
    load(f);
  };
  const clearFilter = () => {
    setFormationFilter("");
    setAppliedFilter("");
    load("");
  };

  const requestIntro = async (case_id) => {
    if (!window.confirm("Unlock this case? If you have a free unlock available it will be auto-applied; otherwise you'll be redirected to Stripe to pay $50.")) return;
    setRequesting(case_id);
    try {
      const r = await api.post(`/payments/unlock/${case_id}/checkout`, { origin_url: window.location.origin });
      if (r.data?.already_unlocked) {
        toast.success("Already unlocked. See My Requests");
        onRefresh();
        return;
      }
      if (r.data?.free_applied) {
        track("payment_completed_unlock", { case_id, promo: "first_unlock_free" });
        toast.success("Free unlock applied. Intro request sent to the case owner");
        onRefresh();
        return;
      }
      if (r.data?.url) {
        track("payment_started_unlock", { case_id, session_id: r.data.session_id });
        window.location.href = r.data.url;
        return;
      }
      toast.error("Could not start checkout");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not request intro");
    } finally {
      setRequesting(null);
    }
  };

  return (
    <div>
      {/* Filter bar */}
      <div className="border border-slate-200 p-4 md:p-5 mb-4 flex flex-wrap items-end gap-3" data-testid="marketplace-filters">
        <div className="flex-1 min-w-[240px]">
          <Label className="label-tiny mb-1.5 block">Filter by state of formation</Label>
          <Input
            value={formationFilter}
            onChange={(e) => setFormationFilter(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") applyFilter(); }}
            placeholder="e.g., Delaware, California, TX"
            className={inputCls}
            data-testid="filter-state-of-formation"
          />
        </div>
        <Button
          type="button"
          onClick={applyFilter}
          className="rounded-none bg-slate-900 hover:bg-slate-800 text-white"
          data-testid="apply-filter-btn"
        >
          Apply
        </Button>
        {appliedFilter && (
          <Button
            type="button"
            variant="outline"
            onClick={clearFilter}
            className="rounded-none border-slate-300"
            data-testid="clear-filter-btn"
          >
            Clear
          </Button>
        )}
        {appliedFilter && (
          <span className="label-tiny text-slate-600 ml-2">
            Showing cases with a party entity formed in <span className="font-medium text-slate-900">{appliedFilter}</span>
          </span>
        )}
      </div>

      {loading ? (
        <div className="border border-slate-200 p-8 label-tiny">Loading cases…</div>
      ) : items.length === 0 ? (
        <div className="border border-slate-200 p-8 text-slate-500" data-testid="marketplace-empty">
          {appliedFilter
            ? `No shared cases match state of formation "${appliedFilter}".`
            : "No shared cases available right now."}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
          {items.map((c, i) => (
            <div key={c.case_id} className={`border border-slate-200 ${i % 2 === 1 ? "md:border-l-0" : ""} -mt-px md:mt-0 p-6`} data-testid={`lawyer-case-${c.case_id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className="rounded-none border-slate-300 font-mono-tech text-[10px]">{c.case_type || "Civil"}</Badge>
                  <Badge variant="outline" className="rounded-none border-slate-300 font-mono-tech text-[10px]">{c.state || "—"}</Badge>
                  {(c.states_of_formation || []).map((sof) => (
                    <Badge key={sof} variant="outline" className="rounded-none border-orange-300 bg-orange-50 text-orange-800 font-mono-tech text-[10px]" data-testid={`sof-badge-${c.case_id}-${sof}`}>
                      Formed in {sof}
                    </Badge>
                  ))}
                </div>
                <span className="label-tiny tabular-nums">{new Date(c.updated_at).toLocaleDateString()}</span>
              </div>
              <div className="font-serif-display text-xl leading-snug text-slate-900 mb-2">Anonymized Summary</div>
              <p className="text-sm text-slate-700 line-clamp-4">{c.summary || "(No summary)"}</p>
              {c.desired_outcome && <p className="text-xs text-slate-500 mt-3"><span className="label-tiny">Desired:</span> {c.desired_outcome}</p>}
              <div className="mt-4">
                <Button onClick={() => requestIntro(c.case_id)} disabled={requesting === c.case_id} className="rounded-none bg-slate-900 hover:bg-slate-800 text-white" data-testid={`request-intro-${c.case_id}`}>
                  {requesting === c.case_id ? "Starting checkout…" : (freeAvailable ? "Use free unlock" : "Unlock & request intro ($50)")}
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MyRequests({ version }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/lawyer/requests");
        setItems(r.data);
      } catch (e) {
        toast.error("Could not load requests");
      } finally {
        setLoading(false);
      }
    })();
  }, [version]);

  if (loading) return <div className="border border-slate-200 p-8 label-tiny">Loading requests…</div>;
  if (items.length === 0) return <div className="border border-slate-200 p-8 text-slate-500">No intro requests yet.</div>;

  const statusGlyph = (s) => (s === "accepted" ? { Icon: CheckCircle, cls: "text-emerald-700" } : s === "declined" ? { Icon: XCircle, cls: "text-red-700" } : { Icon: Clock, cls: "text-amber-700" });

  return (
    <div className="space-y-0">
      {items.map((r) => {
        const g = statusGlyph(r.status);
        const preview = r.status === "accepted" ? r.case_full : r.case_preview;
        return (
          <div key={r.id} className="border border-slate-200 -mt-px p-6" data-testid={`lawyer-request-${r.id}`}>
            <div className="flex items-center gap-3 mb-3">
              <g.Icon weight="duotone" className={`w-6 h-6 ${g.cls}`} />
              <span className="label-tiny">{r.status}</span>
              <span className="text-xs text-slate-500 tabular-nums">· requested {new Date(r.requested_at).toLocaleString()}</span>
            </div>
            {preview && (
              <div>
                <div className="font-serif-display text-xl">{preview.case_title || `${preview.case_type} · ${preview.state}`}</div>
                <p className="text-sm text-slate-700 mt-2">{preview.summary}</p>
              </div>
            )}
            {r.status === "accepted" && r.contact && (
              <div className="mt-4 border-l-2 border-emerald-400 pl-4">
                <div className="label-tiny">Contact revealed</div>
                <div className="text-sm">{r.contact.name} · <a href={`mailto:${r.contact.email}`} className="underline">{r.contact.email}</a></div>
              </div>
            )}
            {r.status === "pending" && (
              <div className="text-xs text-slate-500 mt-3">Waiting for the user to accept or decline your intro request.</div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function MetricsBar({ refreshKey }) {
  const [m, setM] = useState(null);
  useEffect(() => {
    (async () => {
      try { const r = await api.get("/lawyer/metrics"); setM(r.data); } catch (e) {}
    })();
  }, [refreshKey]);
  if (!m) return null;
  const pct = (m.conversion_rate || 0) * 100;
  const cells = [
    { label: "Case views", value: m.case_views, testid: "metric-views" },
    { label: "Pending unlocks", value: m.pending_unlocks, testid: "metric-pending" },
    { label: "Accepted intros", value: m.accepted_intros, testid: "metric-accepted" },
    { label: "Conversion rate", value: `${pct.toFixed(1)}%`, testid: "metric-conversion" },
  ];
  return (
    <div className="border border-slate-200 mb-6 grid grid-cols-2 md:grid-cols-4" data-testid="lawyer-metrics-bar">
      {cells.map((c, i) => (
        <div key={c.label} className={`p-5 ${i > 0 ? "border-l border-slate-200" : ""}`} data-testid={c.testid}>
          <div className="label-tiny">{c.label}</div>
          <div className="font-serif-display text-3xl text-slate-900 tabular-nums mt-1">{c.value}</div>
        </div>
      ))}
    </div>
  );
}

function FreeUnlockBanner({ profile }) {
  const remaining = Number(profile?.free_unlocks_remaining || 0);
  const expIso = profile?.free_unlock_expires_at;
  if (remaining <= 0 || !expIso) return null;
  const exp = new Date(expIso);
  const days = Math.max(0, Math.ceil((exp.getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
  if (exp.getTime() < Date.now()) return null;
  return (
    <div className="border border-emerald-200 bg-emerald-50 p-4 mb-6 flex items-center gap-3" data-testid="free-unlock-banner">
      <Gift weight="duotone" className="w-6 h-6 text-emerald-700" />
      <div className="flex-1">
        <div className="font-medium text-emerald-900">You have {remaining} free unlock available</div>
        <div className="text-xs text-emerald-800">Auto-applied on your next case unlock · expires in {days} day{days === 1 ? "" : "s"} · $50 value</div>
      </div>
    </div>
  );
}

export default function LawyerPortal() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requestsVersion, setRequestsVersion] = useState(0);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const r = await api.get("/lawyer/profile");
      setProfile(r.data);
    } catch (e) {
      toast.error("Could not load profile");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadProfile(); }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <AppHeader />
        <div className="max-w-[1400px] mx-auto px-6 py-16 label-tiny">Loading…</div>
      </div>
    );
  }

  const isLawyer = profile?.role === "lawyer";
  const isSubscribed = profile?.is_subscribed;

  return (
    <div className="min-h-screen bg-white">
      <DisclaimerBanner />
      <AppHeader />

      <main className="max-w-[1400px] mx-auto px-6 py-10">
        <div className="mb-8 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="label-tiny mb-2">Attorney Portal</div>
            <h1 className="font-serif-display text-4xl md:text-5xl font-light text-slate-900 leading-tight">
              <Gavel weight="duotone" className="inline-block w-10 h-10 mr-2 -mt-2 text-slate-900" /> Lawyer Portal
            </h1>
          </div>
          <Link to="/dashboard" className="text-sm text-slate-600 underline" data-testid="back-to-user-dashboard">Back to my cases</Link>
        </div>

        {!isLawyer ? (
          <OnboardForm onDone={loadProfile} />
        ) : (!isSubscribed || profile?.lawyer_status !== "approved") ? (
          <SubscribeGate onSubscribe={loadProfile} lawyerStatus={profile?.lawyer_status} />
        ) : (
          <>
            <FreeUnlockBanner profile={profile} />
            <MetricsBar refreshKey={requestsVersion} />
            <Tabs defaultValue="browse">
            <TabsList className="bg-transparent p-0 h-auto rounded-none border border-slate-200 w-full justify-start" data-testid="lawyer-tabs">
              <TabsTrigger value="browse" className="rounded-none border-r border-slate-200 px-5 py-3 text-xs font-mono-tech uppercase tracking-wider data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="lawyer-tab-browse">
                <Suitcase className="w-4 h-4 mr-2" /> Browse Cases
              </TabsTrigger>
              <TabsTrigger value="requests" className="rounded-none px-5 py-3 text-xs font-mono-tech uppercase tracking-wider data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="lawyer-tab-requests">
                <EnvelopeOpen className="w-4 h-4 mr-2" /> My Requests
              </TabsTrigger>
            </TabsList>
            <div className="border border-t-0 border-slate-200 p-6 md:p-10 bg-white">
              <TabsContent value="browse" className="mt-0">
                <CaseBrowser onRefresh={() => { setRequestsVersion((v) => v + 1); loadProfile(); }} freeAvailable={Number(profile?.free_unlocks_remaining || 0) > 0 && profile?.free_unlock_expires_at && new Date(profile.free_unlock_expires_at) > new Date()} />
              </TabsContent>
              <TabsContent value="requests" className="mt-0">
                <MyRequests version={requestsVersion} />
              </TabsContent>
            </div>
          </Tabs>
          </>
        )}

        <div className="mt-10 text-xs text-slate-500 border-t border-slate-200 pt-6 flex items-center gap-2" data-testid="billing-notice">
          <Warning className="w-4 h-4" />
          Payments are processed securely by <strong className="text-slate-900">Stripe</strong>. Subscription $99/mo, per-unlock $50.
        </div>
        <MarketplaceNotice className="mt-4" />
      </main>
      <SiteFooter />
    </div>
  );
}
